import type { Reminder, UpdateReminderBody } from '@kanto/shared';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { errorText } from '../api/client';
import {
  useArchiveReminder,
  useCreateReminder,
  useReminders,
  useRestoreReminder,
  useUpdateReminder,
} from '../api/hooks';
import { AppShell } from '../components/AppShell';
import { OfflineNotice } from '../components/OfflineNotice';
import { Panel } from '../components/Panel';
import { PushToggle } from '../components/PushToggle';
import { ReminderComposer } from '../components/ReminderComposer';
import { ReminderRow } from '../components/ReminderRow';
import { TagFacet } from '../components/TagFacet';
import { dayOf, isPast, isToday } from '../lib/dates';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * The reminders, and the six states this screen can be in — a legible sibling of
 * `/todos` rather than a second design of the same idea.
 *
 * What it adds is `PushToggle`, near the top, because the notification is the
 * whole difference between this module and a todo list with a clock in it. A
 * setting nobody finds is a setting nobody turns on, and a reminder that does
 * not ring is a row waiting for somebody to look at it.
 *
 * There is no archive screen here either, for the reason there is none there:
 * archiving keeps the row, the strip below offers the way back, and the screen
 * that lists what has been archived arrives when something has been archived
 * long enough to be missed (D260814j).
 */

/**
 * Next, Today, All. Computed here rather than asked of the server: the whole
 * list is a few dozen rows, and a filter that costs a round trip is a filter that
 * shows a spinner for a question already answered on screen.
 *
 * **Today includes what is late**, for the reason the todos screen says it does —
 * a list called Today that hid this morning's missed reminder would be the one
 * thing a reminder is for, quietly not working. Late is a *minute* here rather
 * than a day: 14:30 is late at 14:31.
 */
const FILTERS = [
  { id: 'next', label: 'reminders.filterNext', keep: (r: Reminder) => r.doneAt === null },
  {
    id: 'today',
    label: 'reminders.filterToday',
    keep: (r: Reminder) => r.doneAt === null && (isToday(dayOf(r.remindAt)) || isPast(r.remindAt)),
  },
  { id: 'all', label: 'reminders.filterAll', keep: () => true },
] satisfies { id: string; label: MessageKey; keep: (reminder: Reminder) => boolean }[];

type FilterId = (typeof FILTERS)[number]['id'];

/**
 * Uneven on purpose. Four bars of one length read as a table being drawn; four of
 * different lengths read as sentences that have not arrived yet, which is what
 * they stand in for.
 */
const SKELETON_WIDTHS = ['62%', '45%', '70%', '52%'];

export default function RemindersPage(): ReactElement {
  const t = useT();
  const { data, error, isPending, fetchStatus, refetch } = useReminders();
  const create = useCreateReminder();
  const update = useUpdateReminder();
  const archive = useArchiveReminder();
  const restore = useRestoreReminder();

  /** The row a search hit named, read from the address as on the todos screen. */
  const [params] = useSearchParams();
  const highlight = params.get('highlight');

  const [filter, setFilter] = useState<FilterId>('next');
  /** The tag facet, cumulative with the filter above — as on the todos screen. */
  const [tag, setTag] = useState<string | null>(null);
  /** Archived in this sitting, and therefore still offered back. */
  const [archived, setArchived] = useState<string[]>([]);

  const reminders = data?.reminders ?? [];
  const done = reminders.filter((reminder) => reminder.doneAt !== null);
  const allDone = reminders.length > 0 && done.length === reminders.length;

  /** The tags on screen, plus the selected one even after its last row has left. */
  const facetTags = [
    ...new Set([...reminders.flatMap((reminder) => reminder.tags), ...(tag === null ? [] : [tag])]),
  ].sort();

  const tagged =
    tag === null ? reminders : reminders.filter((reminder) => reminder.tags.includes(tag));
  const shown = tagged.filter(FILTERS.find((entry) => entry.id === filter)!.keep);
  const shownStanding = shown.filter((reminder) => reminder.doneAt === null);
  const shownDone = shown.filter((reminder) => reminder.doneAt !== null);

  const onArchive = (id: string): void => {
    archive.mutate(id);
    // Remembered rather than read back from the list: the row is gone from it by
    // the time this runs, which is the point of archiving optimistically.
    setArchived((ids) => [...ids, id]);
  };

  /**
   * Offline, rather than broken — the difference being that nothing is wrong.
   * The same two signals the todos screen reads, and for the same reason: a
   * fetch TanStack Query paused, and a `navigator.onLine` that is only trusted
   * when it says `false`. See `TodosPage` for the full reasoning; repeating it
   * here would be a second copy to keep in step.
   */
  if ((fetchStatus === 'paused' || !navigator.onLine) && !data) {
    return (
      <Screen>
        <OfflineNotice />
      </Screen>
    );
  }

  if (isPending) {
    return (
      <Screen>
        <ReminderComposer onCreate={() => undefined} pending />
        {/* Four rows the height of real ones. A spinner in the middle of the page
            would be replaced by a list of a different height, and the whole
            screen would jump the moment it arrived. */}
        <ul
          className="mt-4 animate-pulse divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface"
          role="status"
          aria-label={t('common.loadingLabel')}
        >
          {SKELETON_WIDTHS.map((width, row) => (
            <li key={row} className="flex items-center gap-3 px-4 py-3">
              <span className="size-5 shrink-0 rounded bg-line" />
              <span className="h-4 rounded bg-line" style={{ width }} />
            </li>
          ))}
        </ul>
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen>
        <Panel
          tone="alert"
          title={t('reminders.errorTitle')}
          // The server's own words. It knows why it refused, and it has already
          // written that in the language this page asked for.
          body={errorText(error, t('reminders.errorFallback'))}
          icon={
            <path d="M12 8v5M12 16h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          }
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="min-h-11 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim"
            >
              {t('reminders.retry')}
            </button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <ReminderComposer onCreate={(body) => create.mutate(body)} pending={create.isPending} />

      <PushToggle />

      {archived.length > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-line bg-accent-soft px-4 py-2.5 text-sm">
          <span className="flex-1 text-ink-700">{t('reminders.archived', archived.length)}</span>
          <button
            type="button"
            onClick={() => {
              for (const id of archived) restore.mutate(id);
              setArchived([]);
            }}
            className="min-h-9 rounded-lg px-2 font-semibold text-accent hover:underline"
          >
            {t('reminders.undo')}
          </button>
          <button
            type="button"
            onClick={() => setArchived([])}
            aria-label={t('reminders.dismiss')}
            className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 hover:text-ink-900"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      )}

      {reminders.length > 0 && (
        <div
          role="group"
          aria-label={t('reminders.filters')}
          className="mt-4 flex items-center gap-1"
        >
          {FILTERS.map((entry) => {
            const count = tagged.filter(entry.keep).length;
            return (
              <button
                key={entry.id}
                type="button"
                // `aria-pressed` and not a tab: nothing here is a tab panel, and
                // these three are a state the list is in rather than three places
                // to be.
                aria-pressed={entry.id === filter}
                onClick={() => setFilter(entry.id)}
                className={`flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors ${
                  entry.id === filter
                    ? 'bg-accent-soft text-accent'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {t(entry.label)}
                <span className="text-xs tabular-nums opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <TagFacet tags={facetTags} selected={tag} onSelect={setTag} />

      {reminders.length === 0 && (
        <Panel
          tone="quiet"
          dashed
          title={t('reminders.emptyTitle')}
          body={t('reminders.emptyBody')}
          icon={<ClockIcon />}
        />
      )}

      {allDone && filter !== 'all' && (
        <Panel
          tone="accent"
          title={t('reminders.allDoneTitle')}
          body={t('reminders.allDone', done.length)}
          icon={<path d="m5 13 4 4L19 7" />}
        />
      )}

      {/* Reminders exist, and none of them is in this filter. Not one of the
          states above: the list is neither empty nor finished, and an area left
          blank would read as one that failed to draw. */}
      {reminders.length > 0 && shown.length === 0 && !allDone && (
        <Panel
          tone="quiet"
          dashed
          title={t('reminders.nothingHere')}
          body={t('reminders.nothingHereBody')}
          icon={<ClockIcon />}
        />
      )}

      <ReminderList
        reminders={shownStanding}
        onUpdate={update.mutate}
        onArchive={onArchive}
        highlight={highlight}
      />

      {shownDone.length > 0 && (
        <>
          <h2 className="mt-8 mb-2 text-sm font-semibold tracking-wide text-ink-500 uppercase">
            {t('reminders.done')}
          </h2>
          <ReminderList
            reminders={shownDone}
            onUpdate={update.mutate}
            onArchive={onArchive}
            highlight={highlight}
          />
        </>
      )}
    </Screen>
  );
}

/**
 * One card holding rows separated by a hairline, rather than a card per row: a
 * list of cards separates what is meant to be read as one thing.
 */
function ReminderList({
  reminders,
  onUpdate,
  onArchive,
  highlight,
}: {
  reminders: Reminder[];
  onUpdate: (variables: { id: string; patch: UpdateReminderBody }) => void;
  onArchive: (id: string) => void;
  highlight: string | null;
}): ReactElement | null {
  if (reminders.length === 0) return null;

  return (
    <ul className="mt-4 divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
      {reminders.map((reminder) => (
        <ReminderRow
          key={reminder.id}
          reminder={reminder}
          onUpdate={(patch) => onUpdate({ id: reminder.id, patch })}
          onArchive={() => onArchive(reminder.id)}
          highlighted={reminder.id === highlight}
        />
      ))}
    </ul>
  );
}

/** The same mark the rail carries for this module, so the two read as one thing. */
function ClockIcon(): ReactElement {
  return (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  );
}

function Screen({ children }: { children: ReactNode }): ReactElement {
  const t = useT();
  return (
    <AppShell>
      <h1 className="mb-4 text-2xl font-semibold tracking-tight text-ink-900">
        {t('module.reminders')}
      </h1>
      {children}
    </AppShell>
  );
}
